import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { getThumbnailUrl } from '../lib/utils';
import {
  Route,
  BookOpen, 
  Award, 
  ChevronRight,
  Clock,
  CheckCircle2,
  PlayCircle,
  Loader2,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
const API = `${API_BASE}/api`;

export default function LearningPathwaysPage() {
  const { token, user } = useAuth();
  const [pathways, setPathways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPathway, setSelectedPathway] = useState(null);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    fetchPathways();
  }, []);

  const fetchPathways = async () => {
    try {
      const response = await axios.get(`${API}/learning-pathways`);
      setPathways(response.data);
    } catch (error) {
      console.error('Failed to fetch pathways:', error);
      toast.error('Failed to load learning pathways');
    } finally {
      setLoading(false);
    }
  };

  const fetchPathwayDetail = async (pathwayId) => {
    try {
      const response = await axios.get(`${API}/learning-pathways/${pathwayId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSelectedPathway(response.data);
    } catch (error) {
      console.error('Failed to fetch pathway detail:', error);
      toast.error('Failed to load pathway details');
    }
  };

  const handleEnroll = async (pathwayId) => {
    if (!token) {
      toast.error('Please log in to enroll');
      return;
    }

    setEnrolling(true);
    try {
      const response = await axios.post(
        `${API}/learning-pathways/${pathwayId}/enroll`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Enrolled in ${response.data.enrolled_in} courses!`);
      fetchPathwayDetail(pathwayId);
    } catch (error) {
      toast.error('Failed to enroll in pathway');
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-navigate-gold animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-white flex items-center gap-3">
            <Route className="h-8 w-8 text-navigate-gold" />
            Learning Pathways
          </h1>
          <p className="text-zinc-400 mt-2">
            Structured learning journeys to build your expertise step by step
          </p>
        </div>

        {pathways.length === 0 ? (
          <div className="text-center py-16 bg-zinc-800/30 rounded-xl border border-zinc-700">
            <Route className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-xl text-white mb-2">No Learning Pathways Available</h2>
            <p className="text-zinc-400">Check back soon for curated learning journeys</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {pathways.map(pathway => (
              <div 
                key={pathway.pathway_id}
                className="bg-zinc-800/50 border border-zinc-700 rounded-xl overflow-hidden hover:border-zinc-600 transition-colors"
              >
                <div className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                    {/* Pathway Info */}
                    <div className="flex-1">
                      <span className="text-xs text-navigate-gold uppercase tracking-wider font-medium">
                        {pathway.category}
                      </span>
                      <h2 className="text-xl font-bold text-white mt-1 mb-2">{pathway.title}</h2>
                      <p className="text-zinc-400 text-sm line-clamp-2 mb-4">{pathway.description}</p>
                      
                      {/* Stats */}
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <span className="flex items-center gap-2 text-zinc-300">
                          <BookOpen className="h-4 w-4 text-zinc-400" />
                          {pathway.courses?.length || 0} Courses
                        </span>
                        <span className="flex items-center gap-2 text-navigate-gold">
                          <Award className="h-4 w-4" />
                          {pathway.cpd_points_total || 0} CPD Points
                        </span>
                        {pathway.estimated_duration && (
                          <span className="flex items-center gap-2 text-zinc-300">
                            <Clock className="h-4 w-4 text-zinc-400" />
                            {pathway.estimated_duration}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Course Preview */}
                    <div className="lg:w-96">
                      <p className="text-xs text-zinc-500 mb-2">Course Journey</p>
                      <div className="flex items-center gap-1 overflow-x-auto pb-2">
                        {pathway.course_details?.map((course, idx) => (
                          <React.Fragment key={course.course_id}>
                            <div 
                              className="w-14 h-14 shrink-0 rounded-lg bg-zinc-700 overflow-hidden border-2 border-zinc-600 relative group"
                              title={course.title}
                            >
                              {course.thumbnail_url ? (
                                <img src={getThumbnailUrl(course.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <BookOpen className="h-5 w-5 text-zinc-500" />
                                </div>
                              )}
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                <span className="text-white text-xs font-bold">{idx + 1}</span>
                              </div>
                            </div>
                            {idx < pathway.course_details.length - 1 && (
                              <ChevronRight className="h-4 w-4 text-zinc-600 shrink-0" />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>

                    {/* Action */}
                    <div className="lg:text-right">
                      <Link
                        to={`/pathways/${pathway.pathway_id}`}
                        className="inline-flex items-center px-4 py-2 bg-[#1EBFB3] hover:bg-[#25D4C7] text-black rounded-lg font-medium transition-colors"
                      >
                        View Pathway
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pathway Detail Modal */}
        {selectedPathway && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-navigate-surface border border-zinc-800 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-zinc-800 shrink-0">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-xs text-navigate-gold uppercase tracking-wider">
                      {selectedPathway.category}
                    </span>
                    <h2 className="text-2xl font-bold text-white mt-1">{selectedPathway.title}</h2>
                  </div>
                  <button 
                    onClick={() => setSelectedPathway(null)}
                    className="text-zinc-400 hover:text-white"
                  >
                    ✕
                  </button>
                </div>
                <p className="text-zinc-400 mt-2">{selectedPathway.description}</p>
                
                {/* Progress Bar */}
                {selectedPathway.progress && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-zinc-400">Your Progress</span>
                      <span className="text-navigate-gold font-medium">
                        {selectedPathway.progress.completed}/{selectedPathway.progress.total} courses completed
                      </span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-navigate-gold transition-all"
                        style={{ width: `${selectedPathway.progress.percentage}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Course List */}
              <div className="flex-1 overflow-y-auto p-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Courses in this Pathway ({selectedPathway.course_details?.length || 0})
                </h3>
                <div className="space-y-3">
                  {selectedPathway.course_details?.map((course, idx) => (
                    <div 
                      key={course.course_id}
                      className={`flex items-center gap-4 p-4 rounded-xl border ${
                        course.status === 'completed' 
                          ? 'bg-green-500/10 border-green-500/30' 
                          : course.status === 'active'
                          ? 'bg-blue-500/10 border-blue-500/30'
                          : 'bg-zinc-800/50 border-zinc-700'
                      }`}
                    >
                      {/* Step Number */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold shrink-0 ${
                        course.status === 'completed'
                          ? 'bg-green-500 text-white'
                          : course.status === 'active'
                          ? 'bg-blue-500 text-white'
                          : 'bg-zinc-700 text-zinc-400'
                      }`}>
                        {course.status === 'completed' ? (
                          <CheckCircle2 className="h-5 w-5" />
                        ) : (
                          idx + 1
                        )}
                      </div>
                      
                      {/* Course Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium truncate">{course.title}</h4>
                        <div className="flex items-center gap-3 text-xs text-zinc-400 mt-1">
                          <span>{course.category}</span>
                          {course.cpd_points > 0 && (
                            <span className="text-navigate-gold">{course.cpd_points} CPD</span>
                          )}
                        </div>
                      </div>

                      {/* Status/Action */}
                      {course.status === 'completed' ? (
                        <span className="text-xs text-green-400 bg-green-500/20 px-2 py-1 rounded">
                          Completed
                        </span>
                      ) : course.status === 'active' ? (
                        <Link
                          to={`/courses/${course.slug || course.course_id}`}
                          className="text-xs text-blue-400 bg-blue-500/20 px-3 py-1.5 rounded hover:bg-blue-500/30 transition-colors"
                        >
                          Continue
                        </Link>
                      ) : (
                        <Link
                          to={`/courses/${course.slug || course.course_id}`}
                          className="text-xs text-zinc-400 hover:text-white transition-colors"
                        >
                          View
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-zinc-800 shrink-0">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-zinc-400">
                    <span className="text-navigate-gold font-medium">{selectedPathway.cpd_points_total}</span> total CPD points
                  </div>
                  {token && selectedPathway.progress?.completed === 0 && (
                    <Button
                      onClick={() => handleEnroll(selectedPathway.pathway_id)}
                      disabled={enrolling}
                      className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
                    >
                      {enrolling ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <PlayCircle className="h-4 w-4 mr-2" />
                      )}
                      Enroll in All Courses
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
